import { useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook pour intégrer Google Analytics 4 avec event tracking
 * + Tracking Supabase pour analytics produit
 *
 * Usage:
 * const { trackEvent, trackPageView, trackException, trackProduct } = useAnalytics();
 * trackEvent('button_click', { button_name: 'subscribe' });
 * trackProduct('project_created', { shape: 'cone' }); // Saved to Supabase
 */

// Session ID pour cette session de navigation
const getSessionId = () => {
  if (typeof window === 'undefined') return null;
  let sessionId = sessionStorage.getItem('illumine_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('illumine_session_id', sessionId);
  }
  return sessionId;
};

export const useAnalytics = () => {
  const { user } = useAuth();
  // Vérifier que gtag est disponible globalement (utiliser useMemo pour stabiliser la référence)
  const gtag = useMemo(() => window.gtag, []);

  /**
   * Track product event to Supabase for internal analytics
   * This works independently of Google Analytics
   */
  const trackProduct = useCallback(async (eventName, eventData = {}) => {
    try {
      await supabase.from('analytics_events').insert({
        user_id: user?.id || null,
        event_name: eventName,
        event_data: eventData,
        session_id: getSessionId(),
        page_url: window.location.pathname,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
      });

      // Also send to GA if available
      if (gtag) {
        gtag('event', eventName, eventData);
      }
    } catch (error) {
      // Silently fail - analytics should never break the app
      if (import.meta.env.DEV) {
        console.warn('Product analytics tracking failed:', error);
      }
    }
  }, [user, gtag]);

  if (!gtag) {
    if (!window.__TRACKING_DISABLED__ && import.meta.env.DEV) {
      console.warn('Google Analytics not loaded. Events will not be tracked.');
    }
    return {
      trackEvent: () => {},
      trackPageView: () => {},
      trackException: () => {},
      trackSignUp: () => {},
      trackPurchase: () => {},
      trackViewItem: () => {},
      trackAddToCart: () => {},
      trackTiming: () => {},
      trackFeatureEvents: {},
      trackProduct, // Supabase tracking still works without GA
    };
  }

  /**
   * Track custom event
   * @param {string} eventName - Event name (e.g., 'button_click', 'form_submit')
   * @param {Object} eventData - Additional event parameters
   */
  const trackEvent = (eventName, eventData = {}) => {
    if (!gtag) return;
    gtag('event', eventName, eventData);
  };

  /**
   * Track page view (auto-tracked but useful for SPA navigation)
   * @param {string} pagePath - Path to track (e.g., '/app', '/blog')
   * @param {string} pageTitle - Page title
   */
  const trackPageView = (pagePath, pageTitle) => {
    if (!gtag) return;
    gtag('config', 'GA_MEASUREMENT_ID', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  };

  /**
   * Track exceptions (errors, warnings)
   * @param {string} description - Error description
   * @param {boolean} fatal - Whether the error is fatal
   */
  const trackException = (description, fatal = false) => {
    if (!gtag) return;
    gtag('event', 'exception', {
      description: description,
      fatal: fatal,
    });
  };

  /**
   * Track user sign-up
   * @param {string} method - Sign-up method (email, google, github, etc.)
   */
  const trackSignUp = (method = 'email') => {
    if (!gtag) return;
    gtag('event', 'sign_up', {
      method: method,
    });
  };

  /**
   * Track purchase conversion
   * @param {Object} purchaseData - Purchase details
   * @param {number} purchaseData.value - Transaction value
   * @param {string} purchaseData.currency - Currency code (e.g., 'EUR')
   * @param {array} purchaseData.items - Array of items purchased
   * @param {string} purchaseData.transaction_id - Transaction ID
   */
  const trackPurchase = (purchaseData) => {
    if (!gtag) return;
    const defaultData = {
      currency: 'EUR',
      value: purchaseData.value || 0,
      items: purchaseData.items || [],
    };
    gtag('event', 'purchase', {
      ...defaultData,
      ...purchaseData,
    });
  };

  /**
   * Track product view
   * @param {Object} itemData - Product details
   * @param {string} itemData.item_id - Product ID
   * @param {string} itemData.item_name - Product name
   * @param {string} itemData.item_category - Category
   */
  const trackViewItem = (itemData) => {
    if (!gtag) return;
    gtag('event', 'view_item', {
      items: [
        {
          item_id: itemData.item_id || '',
          item_name: itemData.item_name || '',
          item_category: itemData.item_category || '',
          price: itemData.price || 0,
          currency: 'EUR',
        },
      ],
    });
  };

  /**
   * Track add to cart
   * @param {Object} itemData - Item details
   */
  const trackAddToCart = (itemData) => {
    if (!gtag) return;
    gtag('event', 'add_to_cart', {
      items: [
        {
          item_id: itemData.item_id || '',
          item_name: itemData.item_name || '',
          item_category: itemData.item_category || '',
          price: itemData.price || 0,
          quantity: itemData.quantity || 1,
          currency: 'EUR',
        },
      ],
      value: (itemData.price || 0) * (itemData.quantity || 1),
      currency: 'EUR',
    });
  };

  /**
   * Track custom timing (for performance metrics)
   * @param {string} name - Timing name
   * @param {number} value - Duration in milliseconds
   * @param {string} category - Category
   */
  const trackTiming = (name, value, category = 'performance') => {
    if (!gtag) return;
    gtag('event', 'timing_complete', {
      name: name,
      value: value,
      event_category: category,
    });
  };

  // Event tracking for specific features
  const trackFeatureEvents = {
    // 3D Preview events
    threeDPreviewLoaded: () => trackEvent('three_d_preview_loaded'),
    threeDPreviewRotate: (duration) => trackEvent('three_d_rotate', { duration_ms: duration }),

    // Design events
    patternCreated: (shape, material) => trackEvent('pattern_created', { shape, material }),
    patternExported: (format) => trackEvent('pattern_exported', { export_format: format }),
    patternShared: (method) => trackEvent('pattern_shared', { share_method: method }),

    // Blog events
    blogArticleRead: (title, readingTime) => trackEvent('article_read', { article_title: title, reading_time_seconds: readingTime }),
    blogArticleShared: (title, platform) => trackEvent('article_shared', { article_title: title, platform }),

    // Form events
    formSubmitted: (formName) => trackEvent('form_submitted', { form_name: formName }),
    formError: (formName, errorMessage) => trackEvent('form_error', { form_name: formName, error: errorMessage }),

    // Demo events
    demoStarted: () => trackEvent('demo_started'),
    demoExpired: () => trackEvent('demo_expired'),
    demoUpgraded: () => trackEvent('demo_upgraded'),

    // Subscription events
    subscriptionStarted: (plan) => trackEvent('subscription_started', { plan }),
    subscriptionCanceled: (reason) => trackEvent('subscription_canceled', { cancellation_reason: reason }),
  };

  return {
    trackEvent,
    trackPageView,
    trackException,
    trackSignUp,
    trackPurchase,
    trackViewItem,
    trackAddToCart,
    trackTiming,
    trackFeatureEvents,
    trackProduct, // New: Supabase-based product analytics
  };
};

/**
 * Alternative hook using the gtag function directly
 * Usage: const { gtag } = useGtag();
 * gtag('event', 'custom_event', { param: 'value' });
 */
export const useGtag = () => {
  const gtag = window.gtag || (() => {});
  return { gtag };
};

export default useAnalytics;
